// Setting a password, from an invitation or a reset.
//
// New against the legacy application, which left passwords to Keycloak; drawn
// in the sign-in page's card so that it looks like the rest of the way in.
//
// The screen asks the server what the link is for before spending it, so that
// somebody who has never had a password is told "set one" rather than "choose
// a new one", and so that the company's own rule can be shown before they type
// rather than after they are refused.

import { Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CircularProgress, Typography } from "@mui/material";
import { api, ApiError, type TokenInfo } from "~/api";
import { useT } from "~/i18n";
import { CardButton, CardField, CardHead, SignInFrame } from "./signin";

export function PasswordPage() {
  const t = useT();
  const navigate = useNavigate();
  const token = new URLSearchParams(window.location.search).get("token") ?? "";

  const [info, setInfo] = useState<TokenInfo | null>(null);
  const [gone, setGone] = useState(false);
  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setGone(true);
      return;
    }
    api<TokenInfo>(`/api/portal/auth/password/token?token=${encodeURIComponent(token)}`)
      .then(setInfo)
      .catch(() => setGone(true));
  }, [token]);

  async function submit() {
    if (password !== repeat) {
      setError(t("passwords-differ"));
      return;
    }
    setBusy(true);
    setError("");
    try {
      await api("/api/portal/auth/password/reset", { method: "POST", body: { token, password } });
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : t("something-went-wrong"));
    } finally {
      setBusy(false);
    }
  }

  const note = (text: string, error = false) => (
    <Typography sx={{ mt: 3, maxWidth: 320, textAlign: "center", fontSize: 14, color: error ? "error.main" : "text.secondary" }}>
      {text}
    </Typography>
  );

  return (
    <SignInFrame apps={false}>
      <CardHead />
      {gone ? (
        <Fragment>
          {note(t("link-expired"), true)}
          <CardButton onClick={() => navigate("/login")}>{t("login")}</CardButton>
        </Fragment>
      ) : done ? (
        <Fragment>
          {note(t("password-set"))}
          <CardButton onClick={() => navigate(`/login?email=${encodeURIComponent(info?.email ?? "")}`)}>{t("login")}</CardButton>
        </Fragment>
      ) : !info ? (
        <CircularProgress sx={{ my: 4 }} />
      ) : (
        <Fragment>
          <Typography sx={{ mt: 3, fontSize: 20, fontWeight: 700 }}>
            {info.purpose === "invite" ? t("set-password") : t("new-password")}
          </Typography>
          <Typography sx={{ fontSize: 14, color: "text.secondary" }}>{info.email}</Typography>
          {info.policyHint ? note(info.policyHint) : null}
          <CardField
            label={t("new-password")}
            type="password"
            autoComplete="new-password"
            autoFocus
            value={password}
            onChange={setPassword}
          />
          <CardField
            label={t("repeat-password")}
            type="password"
            autoComplete="new-password"
            value={repeat}
            onChange={setRepeat}
            error={!!error}
            helperText={error}
            onEnter={submit}
          />
          <CardButton onClick={submit} disabled={busy || !password} error={!!error}>
            {t("save")}
          </CardButton>
        </Fragment>
      )}
    </SignInFrame>
  );
}
